import React from 'react';
import { medicalTeam, credentialBadges } from '../../data/mock';
import { Stethoscope, Heart, Apple, Users, Award, GraduationCap } from 'lucide-react';

const ICON_MAP = { Stethoscope, Heart, Apple, Users };

const MedicalTeamSection = () => {
  return (
    <section
      id="team"
      className="py-16 sm:py-20 lg:py-24 relative overflow-hidden bg-slate-900"
      data-testid="team-section"
    >
      <div className="absolute top-0 left-1/4 w-72 h-72 bg-blue-600/10 rounded-full blur-3xl" aria-hidden="true"></div>
      <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-emerald-600/10 rounded-full blur-3xl" aria-hidden="true"></div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-10 sm:mb-14 max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-4">
            <Stethoscope className="w-4 h-4" aria-hidden="true" />
            Doctor-Led Medical Team
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Meet The Experts Behind <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Your Journey</span>
          </h2>
          <p className="text-slate-400 text-sm sm:text-base">
            Evidence-based, preventive healthcare led by qualified medical professionals.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto mb-10 sm:mb-12">
          {medicalTeam.map((m) => {
            const Icon = ICON_MAP[m.icon] || Stethoscope;
            return (
              <article
                key={m.id}
                className={`group relative bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 sm:p-7 border ${m.isLead ? 'border-blue-500/40' : m.isNetwork ? 'border-emerald-500/30' : 'border-slate-700/60'} hover:border-slate-500 transition-all overflow-hidden`}
                data-testid={`team-member-${m.id}`}
              >
                {/* Decorative glow */}
                <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl ${m.isLead ? 'bg-blue-500/15' : m.isNetwork ? 'bg-emerald-500/15' : 'bg-cyan-500/10'} group-hover:opacity-70 transition-opacity`} aria-hidden="true"></div>

                <div className="relative flex items-start gap-4 mb-4">
                  <div className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center border ${m.isLead ? 'bg-blue-500/15 border-blue-500/40 text-blue-300' : m.isNetwork ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'}`}>
                    <Icon className="w-7 h-7" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-white leading-snug">{m.name}</h3>
                    <p className={`text-sm font-semibold ${m.isLead ? 'text-blue-300' : m.isNetwork ? 'text-emerald-300' : 'text-cyan-300'} mt-0.5`}>{m.role}</p>
                  </div>
                </div>

                <p className="relative text-slate-400 text-xs sm:text-sm font-medium mb-3">{m.specialty}</p>

                {m.qualifications && (
                  <div className="relative inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full mb-4">
                    <GraduationCap className="w-3.5 h-3.5 text-amber-300" aria-hidden="true" />
                    <span className="text-amber-200 text-xs font-semibold">{m.qualifications}</span>
                  </div>
                )}

                {m.focus && (
                  <p className="relative text-slate-300 text-sm leading-relaxed">{m.focus}</p>
                )}
              </article>
            );
          })}
        </div>

        {/* Credential Badges */}
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-slate-500 text-xs sm:text-sm mb-4 uppercase tracking-wider">Our team's credentials</p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {credentialBadges.map((badge, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-800/60 border border-slate-700 rounded-full text-slate-300 text-xs sm:text-sm"
                data-testid={`credential-badge-${i}`}
              >
                <Award className="w-3.5 h-3.5 text-amber-400" aria-hidden="true" />
                {badge}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MedicalTeamSection;
